import { Box, Divider, SxProps } from '@mui/material';
import { useAuth0 } from '@auth0/auth0-react';
import { useAtomValue } from 'jotai';
import { useMemo } from 'react';
import { GenericAccordion } from '@/components/shared/GenericAccordion';
import * as state from '@/components/Analysis/state';
import {
  BlisterHeightType,
  Maybe,
  UnitsEnum,
  useStructureWallLossFactorQuery,
} from '@/__generated__/graphql';

import { FieldViewer } from '../../FieldViewer';
import {
  AitUserVerifiedPoiView,
  InspectorVerifiedFieldsAccordian,
  InspectorVerifiedPoiView,
} from './DetailsTabViews';
import { PointOfInterest, PointOfInterestTemplate } from '@/__generated__/graphql';
import {
  convertInchesToMillimeters,
  convertMillimetersToInches,
  formatPercentage,
  localiseMeasurement,
} from '@/utils/unitSystem';
import { useFeatureFlag } from '@/hooks';
import { useBlisterFieldConfig } from '@/hooks/useBlisterFieldConfig';
import { TemplateFieldTypeEnum } from '@/types';
import {
  UNCERTAIN_PROFILE_LABEL,
  PIT_DEPTH_LABEL,
  BLISTER_HEIGHT_LABEL,
  BLISTER_TYPE,
  PIT_TYPE,
  CALCULATION_METHOD_LABEL,
  heightTypeMapping,
  BLISTER_TOOL_HEIGHT_LABEL,
  PDR_HEIGHT_LABEL,
  SC_CATEGORY_KEY,
  BLANK_SPACE,
  NOT_APPLICABLE_LABEL,
} from '@/constants';
import { UnitSystemEnum } from '@/__generated__/graphql';
import { useNavbarHeight } from '@/hooks/useNavbarHeight';
import { useGetStructureId } from '@/hooks/useGetStructureId';
import { BlisterHeightMethodSelector } from './BlisterHeightMethodSelector';
import { getBlisterHeightValueDisplay } from '@/utils/pointOfInterest';
import { useIsPoiWorkScope } from '@/hooks/useIsPoiWorkScope';
import { DetailsOptionMenu } from './DetailsOptionMenu';

const accordianProps = {
  headingStyles: {
    fontWeight: 'bold',
  },
};

type Props = {
  disableEdit?: boolean;
  selectedPoiInfo: PointOfInterest;
  sx?: SxProps;
};

const boxRootStyle = {
  paddingBottom: '2.4rem',
  overflowY: 'auto',
  msOverflowStyle: 'none',
  scrollbarWidth: 'none',
  '&::-webkit-scrollbar': { display: 'none' },
};

type BlisterHeightFieldsProps = {
  isAutoPOI: boolean;
  unitSystem: UnitSystemEnum;
  estimatedHeight?: Maybe<number>;
  pdrHeight?: Maybe<number>;
  heightTypeValue: string;
  blisterFieldConfig: {
    estimatedHeight?: { name: string; hidden: boolean };
    pdrHeight?: { name: string; hidden: boolean };
    heightType?: { name: string; hidden: boolean };
  };
};

type FieldItem = {
  value: string;
  label: string;
};

const getBlisterHeightFields = ({
  isAutoPOI,
  unitSystem,
  estimatedHeight,
  pdrHeight,
  heightTypeValue,
  blisterFieldConfig,
}: BlisterHeightFieldsProps): FieldItem[] => {
  const result: FieldItem[] = [];

  // Add estimated height and PDR height fields for auto-generated POIs
  if (isAutoPOI) {
    // Add blister tool height field if not hidden
    if (!blisterFieldConfig.estimatedHeight?.hidden) {
      result.push({
        value: getBlisterHeightValueDisplay(unitSystem, estimatedHeight),
        label: blisterFieldConfig.estimatedHeight?.name || BLISTER_TOOL_HEIGHT_LABEL,
      });
    }

    // Add PDR height field if not hidden
    if (!blisterFieldConfig.pdrHeight?.hidden) {
      result.push({
        value: getBlisterHeightValueDisplay(unitSystem, pdrHeight),
        label: blisterFieldConfig.pdrHeight?.name || PDR_HEIGHT_LABEL,
      });
    }
  }

  // Add calculation method field if height type is not uncertain
  if (
    isAutoPOI &&
    heightTypeValue !== heightTypeMapping[BlisterHeightType.Uncertain] &&
    !blisterFieldConfig.heightType?.hidden
  ) {
    result.push({
      value: heightTypeValue,
      label: blisterFieldConfig.heightType?.name || CALCULATION_METHOD_LABEL,
    });
  }

  return result;
};

export const DetailsTab = ({ disableEdit, selectedPoiInfo, sx }: Props) => {
  const { user } = useAuth0();
  const allPointOfInterestTemplates = useAtomValue(state.allPointOfInterestTemplates);
  const structureBlisterHeightType = useAtomValue(state.structureBlisterHeightType);
  const selectedAssemblyId = useAtomValue(state.selectedAssemblyId);
  const unitSystem = useAtomValue(state.unitSystem);
  const navbarHeight = useNavbarHeight();
  const structureId = useGetStructureId();
  const { checkByType } = useIsPoiWorkScope();
  const blisterFieldConfig = useBlisterFieldConfig({ type: selectedPoiInfo.type });

  const { data: structureData } = useStructureWallLossFactorQuery({
    variables: { structureId },
    skip: !structureId,
  });

  const structureWallLossFactor = useMemo(() => {
    const wallLossFactor = structureData?.structure?.wallLossFactor;
    if (!wallLossFactor) return 'N/A';
    return String(wallLossFactor);
  }, [structureData]);

  const templateOfSelectedPoi = useMemo(() => {
    return allPointOfInterestTemplates.find(({ id }) => id === selectedPoiInfo.template?.id);
  }, [allPointOfInterestTemplates, selectedPoiInfo]);

  // Need to render the comments separetely
  const templateFields = useMemo(() => {
    const fields =
      templateOfSelectedPoi?.fields?.filter(
        (field) => !field.isHidden && field.name !== 'Comments'
      ) || [];

    // Sort by displayOrder if available
    return [...fields].sort((a, b) => {
      const DEFAULT_ORDER = Number.MAX_SAFE_INTEGER;
      const orderA = a.displayConfig?.displayOrder ?? DEFAULT_ORDER;
      const orderB = b.displayConfig?.displayOrder ?? DEFAULT_ORDER;
      return orderA - orderB;
    });
  }, [templateOfSelectedPoi]);

  const templateFieldsWithValues: Array<{
    label: string;
    value: string;
    type?: TemplateFieldTypeEnum;
    valueTooltip?: string;
    unit?: UnitsEnum | null | undefined;
  }> = useMemo(() => {
    let blisterInfo: { value: string; label: string }[] = [];
    if (selectedPoiInfo.blister) {
      const {
        maxHeight,
        wallLoss,
        residualCorrosionAllowance,
        residualThickness,
        heightType,
        isManuallyOverridden,
        estimatedHeight,
        pdrHeight,
      } = selectedPoiInfo.blister || {};
      const isAutoPOI = selectedPoiInfo.isAutoGenerated;
      const enableWallLossFactor = selectedPoiInfo.type !== PIT_TYPE;
      const label = selectedPoiInfo.type === BLISTER_TYPE ? BLISTER_HEIGHT_LABEL : PIT_DEPTH_LABEL;
      const heightTypeValue = isManuallyOverridden
        ? heightTypeMapping[heightType ?? BlisterHeightType.BlisterTool]
        : heightTypeMapping[structureBlisterHeightType ?? BlisterHeightType.BlisterTool];

      blisterInfo = [
        ...getBlisterHeightFields({
          isAutoPOI: isAutoPOI ?? false,
          unitSystem,
          estimatedHeight,
          pdrHeight,
          heightTypeValue,
          blisterFieldConfig,
        }),

        // Max Height field
        ...(!blisterFieldConfig.maxHeight?.hidden
          ? [
              {
                value:
                  localiseMeasurement({
                    unitSystem,
                    value: maxHeight || undefined,
                    showUnitLabel: true,
                    sourceUnit: UnitsEnum.Meter,
                    displayAsUnit: UnitsEnum.Millimeter,
                    fallbackLabel: UNCERTAIN_PROFILE_LABEL,
                  }) ?? UNCERTAIN_PROFILE_LABEL,
                label: blisterFieldConfig.maxHeight?.name || label,
              },
            ]
          : []),

        // Wall Loss field
        ...(!blisterFieldConfig.wallLoss?.hidden
          ? [
              {
                value:
                  wallLoss?.value === null || wallLoss?.value === undefined
                    ? NOT_APPLICABLE_LABEL
                    : localiseMeasurement({
                        unitSystem,
                        value: wallLoss.value,
                        showUnitLabel: true,
                        sourceUnit: UnitsEnum.Meter,
                        displayAsUnit: UnitsEnum.Millimeter,
                      }) +
                      (wallLoss?.percentage !== null && wallLoss?.percentage !== undefined
                        ? formatPercentage(wallLoss.percentage)
                        : ''),
                label: blisterFieldConfig.wallLoss?.name || 'Estimated Wall Loss',
              },
            ]
          : []),

        // Wall Loss Factor (always shown if enabled)
        ...(enableWallLossFactor
          ? [{ value: structureWallLossFactor, label: 'Estimated Wall Loss Factor' }]
          : []),

        // Residual Corrosion Allowance field
        ...(!blisterFieldConfig.residualCorrosionAllowance?.hidden
          ? [
              {
                value:
                  residualCorrosionAllowance?.value === null ||
                  residualCorrosionAllowance?.value === undefined
                    ? NOT_APPLICABLE_LABEL
                    : localiseMeasurement({
                        unitSystem,
                        value: residualCorrosionAllowance.value,
                        showUnitLabel: true,
                        sourceUnit: UnitsEnum.Meter,
                        displayAsUnit: UnitsEnum.Millimeter,
                      }) +
                      (residualCorrosionAllowance?.percentage !== null &&
                      residualCorrosionAllowance?.percentage !== undefined
                        ? formatPercentage(residualCorrosionAllowance.percentage)
                        : ''),
                label:
                  blisterFieldConfig.residualCorrosionAllowance?.name ||
                  'Residual Corrosion Allowance',
              },
            ]
          : []),

        // Residual Thickness field
        ...(!blisterFieldConfig.residualThickness?.hidden
          ? [
              {
                value:
                  residualThickness?.value === null || residualThickness?.value === undefined
                    ? NOT_APPLICABLE_LABEL
                    : localiseMeasurement({
                        unitSystem,
                        value: residualThickness.value,
                        showUnitLabel: true,
                        sourceUnit: UnitsEnum.Meter,
                        displayAsUnit: UnitsEnum.Millimeter,
                      }) +
                      (residualThickness?.percentage !== null &&
                      residualThickness?.percentage !== undefined
                        ? formatPercentage(residualThickness.percentage)
                        : ''),
                label: blisterFieldConfig.residualThickness?.name || 'Residual Thickness',
              },
            ]
          : []),

        ...(!blisterFieldConfig.measurementConfidence?.hidden
          ? [
              {
                value: selectedPoiInfo.blister.measurementConfidence ?? NOT_APPLICABLE_LABEL,
                label: blisterFieldConfig.measurementConfidence?.name || 'Measurement Confidence',
              },
            ]
          : []),
        ...(!blisterFieldConfig.distanceConfidence?.hidden
          ? [
              {
                value: selectedPoiInfo.blister.distanceConfidence ?? NOT_APPLICABLE_LABEL,
                label: blisterFieldConfig.distanceConfidence?.name || 'Distance Confidence',
              },
            ]
          : []),
      ];
    }

    const templateInfo = templateFields?.map((field) => {
      const fallbackDeckFromTemplateFieldData = (() => {
        const entries = Object.entries(selectedPoiInfo.templateFieldData ?? {});
        const matched = entries.find(([key, val]) => key.toLowerCase().includes('deck') && !!val);
        return matched?.[1];
      })();
      // First try templateFieldData, then fall back to templateData array
      let value =
        selectedPoiInfo.templateFieldData?.[field.key ?? ''] ??
        selectedPoiInfo.templateData?.find((td) => td.id === field.id)?.value ??
        'N/A';
      const normalizedFieldName = (field?.name ?? '').trim().toLowerCase();
      if (value === 'N/A' && normalizedFieldName === 'paint block') {
        value = selectedPoiInfo.paintRegion?.name ?? 'N/A';
      }
      if (
        value === 'N/A' &&
        (normalizedFieldName === 'deck' || normalizedFieldName === 'deck name')
      ) {
        value =
          selectedPoiInfo.paintRegion?.deck?.name ?? fallbackDeckFromTemplateFieldData ?? 'N/A';
      }

      if (field.key === SC_CATEGORY_KEY && (!value || value === 'N/A')) {
        value = selectedPoiInfo.blister?.scCategory || 'N/A';
      }

      if (field.detailFieldKey) {
        const detailValue = selectedPoiInfo.templateFieldData?.[field.detailFieldKey];

        return {
          value,
          label: field?.name || 'N/A',
          type: field?.type,
          valueTooltip: value,
          ...(detailValue ? { subtitle: detailValue } : {}),
        };
      }

      if (
        field.type === TemplateFieldTypeEnum.MeasurementPaintLength &&
        selectedPoiInfo?.lineMeasurement?.points
      ) {
        const points = selectedPoiInfo?.lineMeasurement?.points;
        if (!Array.isArray(points) || points.length < 2) {
          value = '';
        }
      }

      if (
        field.type === TemplateFieldTypeEnum.MeasurementPaintDiameter &&
        (!value || value === NOT_APPLICABLE_LABEL || value === BLANK_SPACE)
      ) {
        value = selectedPoiInfo.assembly.pipeSpec?.size ?? NOT_APPLICABLE_LABEL;
      }

      return {
        value,
        label: field?.name || 'N/A',
        type: field?.type,
        valueTooltip: value,
        unit: field?.unit,
      };
    });
    return [...blisterInfo, ...templateInfo];
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    selectedPoiInfo.assembly.pipeSpec?.size,
    selectedPoiInfo.blister,
    selectedPoiInfo.isAutoGenerated,
    selectedPoiInfo?.lineMeasurement?.points,
    selectedPoiInfo.templateFieldData,
    selectedPoiInfo.templateData,
    selectedPoiInfo.type,
    structureBlisterHeightType,
    structureWallLossFactor,
    templateFields,
    unitSystem,
    templateOfSelectedPoi?.fields,
    blisterFieldConfig,
  ]);

  const verifiedFieldsWithValues: Array<{ label: string; value: string }> = useMemo(() => {
    // Filter fields to only include those with verified values
    const fieldsWithVerifiedValues = templateFields?.filter((field) => {
      // Check if field has a verified value in templateFieldData
      if (field.key && selectedPoiInfo.templateFieldData) {
        const verifiedFieldKey = `verified_${field.key}`;
        const fieldValue = selectedPoiInfo.templateFieldData[verifiedFieldKey];
        if (fieldValue && fieldValue !== 'N/A') {
          return true;
        }
      }

      // Fall back to checking templateData if needed
      const verifiedValue = selectedPoiInfo.templateData?.find(
        (td) => td.id === field.id
      )?.verifiedValue;
      return verifiedValue && verifiedValue !== 'N/A';
    });

    const verifiedData =
      fieldsWithVerifiedValues?.map((field) => {
        // First check templateFieldData for verified value
        let value = 'N/A';
        if (field.key && selectedPoiInfo.templateFieldData) {
          const verifiedFieldKey = `verified_${field.key}`;
          const fieldValue = selectedPoiInfo.templateFieldData[verifiedFieldKey];
          if (fieldValue) {
            value = fieldValue;
          }
        }

        // Fall back to templateData if needed
        if (value === 'N/A') {
          value =
            selectedPoiInfo.templateData?.find((td) => td.id === field.id)?.verifiedValue ?? 'N/A';
        }

        return {
          value,
          label: field?.name || 'N/A',
        };
      }) || [];

    if (
      selectedPoiInfo?.blister?.verifiedMaxHeight &&
      !blisterFieldConfig.verifiedMaxHeight?.hidden
    ) {
      let verifiedHeight = selectedPoiInfo?.blister.verifiedMaxHeight;
      if (
        selectedPoiInfo?.blister.verifiedMaxHeight.endsWith('"') &&
        unitSystem === UnitSystemEnum.Metric
      ) {
        verifiedHeight =
          convertInchesToMillimeters(
            Number(selectedPoiInfo?.blister.verifiedMaxHeight.slice(0, -1))
          )?.toString() ?? UNCERTAIN_PROFILE_LABEL;
      } else if (
        selectedPoiInfo?.blister.verifiedMaxHeight.endsWith('mm') &&
        unitSystem === UnitSystemEnum.Imperial
      ) {
        verifiedHeight =
          convertMillimetersToInches(
            Number(selectedPoiInfo?.blister.verifiedMaxHeight.slice(0, -2))
          )?.toString() ?? UNCERTAIN_PROFILE_LABEL;
      }

      verifiedData.push({
        label: blisterFieldConfig.verifiedMaxHeight?.name || 'Verified Max Height',
        value: verifiedHeight,
      });
    }

    // Verified Residual Thickness field
    if (
      selectedPoiInfo?.blister?.verifiedResidualThickness &&
      !blisterFieldConfig.verifiedResidualThickness?.hidden
    ) {
      let verifiedThickness = selectedPoiInfo?.blister.verifiedResidualThickness;
      if (
        selectedPoiInfo?.blister.verifiedResidualThickness.endsWith('"') &&
        unitSystem === UnitSystemEnum.Metric
      ) {
        verifiedThickness =
          convertInchesToMillimeters(
            Number(selectedPoiInfo?.blister.verifiedResidualThickness.slice(0, -1))
          )?.toString() ?? UNCERTAIN_PROFILE_LABEL;
      } else if (
        selectedPoiInfo?.blister.verifiedResidualThickness.endsWith('mm') &&
        unitSystem === UnitSystemEnum.Imperial
      ) {
        verifiedThickness =
          convertMillimetersToInches(
            Number(selectedPoiInfo?.blister.verifiedResidualThickness.slice(0, -2))
          )?.toString() ?? UNCERTAIN_PROFILE_LABEL;
      }
      verifiedData.push({
        label: blisterFieldConfig.verifiedResidualThickness?.name || 'Verified Residual Thickness',
        value: verifiedThickness,
      });
    }
    return verifiedData;
  }, [
    selectedPoiInfo?.blister?.verifiedMaxHeight,
    selectedPoiInfo?.blister?.verifiedResidualThickness,
    selectedPoiInfo.templateData,
    selectedPoiInfo.templateFieldData,
    templateFields,
    unitSystem,
    blisterFieldConfig,
  ]);

  const isInspector = useFeatureFlag('api-inspector');
  const isMiEngineer = useFeatureFlag('mi-engineer');
  const canVerifyData = useFeatureFlag([
    'ait-engineer',
    'client',
    'ait-task-lead',
    'api-inspector',
    'mi-engineer',
  ]);

  const hasEditRole = useFeatureFlag(['ait-engineer']);
  const canEditPoiDetails = hasEditRole || isInspector || isMiEngineer;

  const isVerified = selectedPoiInfo.isVerified;

  const isUninitialized = useMemo(() => {
    return selectedPoiInfo.type === BLISTER_TYPE && !selectedPoiInfo.blister;
  }, [selectedPoiInfo.blister, selectedPoiInfo.type]);

  const disableDetailsEdit = useMemo(() => {
    if (selectedPoiInfo?.blister?.isManuallyOverridden) {
      return (
        (selectedPoiInfo.isAutoGenerated &&
          selectedPoiInfo.blister.heightType !== BlisterHeightType.BlisterTool) ||
        (!selectedAssemblyId && !checkByType(selectedPoiInfo.type)) ||
        (disableEdit && !!selectedPoiInfo.blister) ||
        isUninitialized
      );
    } else {
      return (
        (selectedPoiInfo.isAutoGenerated &&
          structureBlisterHeightType !== BlisterHeightType.BlisterTool) ||
        (!selectedAssemblyId && !checkByType(selectedPoiInfo.type)) ||
        (disableEdit && !!selectedPoiInfo.blister) ||
        isUninitialized
      );
    }
  }, [
    selectedPoiInfo.blister,
    selectedPoiInfo.isAutoGenerated,
    selectedPoiInfo.type,
    selectedAssemblyId,
    checkByType,
    disableEdit,
    isUninitialized,
    structureBlisterHeightType,
  ]);
  return (
    <Box sx={{ ...boxRootStyle, height: `calc(100vh - ${navbarHeight + 140}px)` }}>
      {canVerifyData && selectedPoiInfo.isAutoGenerated && selectedPoiInfo.blister && (
        <BlisterHeightMethodSelector pointOfInterest={selectedPoiInfo} key={selectedPoiInfo.id} />
      )}
      {/* If an AIT member sees a POI thats not verified by an inspector yet */}
      {!isInspector && !isVerified ? (
        <AitUserVerifiedPoiView
          pointOfInterest={selectedPoiInfo}
          fields={templateFieldsWithValues}
          template={templateOfSelectedPoi as PointOfInterestTemplate}
          disableEdit={canEditPoiDetails ? false : disableDetailsEdit}
        />
      ) : (
        <GenericAccordion
          summary="Pre Inspection Data"
          summaryStyles={{ fontWeight: 500 }}
          openByDefault
          {...accordianProps}
          endAdornment={
            canEditPoiDetails ? <DetailsOptionMenu selectedPoiInfo={selectedPoiInfo} /> : <></>
          }
        >
          <FieldViewer fields={templateFieldsWithValues} selectedPoiInfo={selectedPoiInfo} />
        </GenericAccordion>
      )}

      {canVerifyData && isVerified && (
        <>
          <Divider />
          <InspectorVerifiedPoiView
            sx={sx}
            fields={verifiedFieldsWithValues}
            pointOfInterest={selectedPoiInfo}
            template={templateOfSelectedPoi as PointOfInterestTemplate}
            disableEdit={canEditPoiDetails ? false : disableDetailsEdit}
          />
        </>
      )}

      {canVerifyData && !isVerified && (
        <InspectorVerifiedFieldsAccordian
          canVerifyData={canVerifyData}
          fields={verifiedFieldsWithValues}
          accordianProps={accordianProps}
          pointOfInterest={selectedPoiInfo}
          template={templateOfSelectedPoi as PointOfInterestTemplate}
        />
      )}
      <Divider />
    </Box>
  );
};
